import { useTranslation } from 'react-i18next';
import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@scio3/ui';
import type { MdmDevice } from '../hooks/useStaffCustomerDevices';

export type MdmDeviceStatus = 'online' | 'stale' | 'offline';

/**
 * Classify a device's online status from its last heartbeat.
 * - fresh (≤ 13 min since last heartbeat) → online
 * - older than 13 min but ≤ 24h → stale (checked in recently, not fully offline)
 * - missing or > 24h → offline
 *
 * The 13-minute fresh window matches the existing `features/screens`
 * heartbeat semantics.
 */
export function classifyDeviceStatus(device: MdmDevice): MdmDeviceStatus {
  const beat = device.lastHeartBeat ? new Date(device.lastHeartBeat).getTime() : 0;
  if (!beat) return 'offline';
  const ageMs = Date.now() - beat;
  const minutes = ageMs / 60000;
  if (minutes <= 13) return 'online';
  if (ageMs <= 24 * 60 * 60 * 1000) return 'stale';
  return 'offline';
}

export function DeviceStatusDot({ device }: { device: MdmDevice }) {
  const { t } = useTranslation();
  const status = classifyDeviceStatus(device);
  const label = status === 'online' ? t('Online') : status === 'stale' ? t('Stale') : t('Offline');

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-label={label}
            data-testid="mdm-device-status-dot"
            className={cn(
              'inline-block h-2 w-2 rounded-full',
              status === 'online' && 'bg-emerald-500',
              status === 'stale' && 'bg-amber-500',
              status === 'offline' && 'bg-muted-foreground',
            )}
          />
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
